/** Operator briefs, cached per id, with ONE batched `?ids=` lookup for the missing ones.
 *
 * The tenant picker, the AppBar scope chip and the scope's URL sync all need to know,
 * for a list of operator ids, each operator's name and client (production + sandbox).
 * Asking `retrieve` per id would cost a request each; the list endpoint answers a whole
 * selection at once (`GET /tour_operators/?ids=1,2`).
 *
 * Briefs live under their own key, `['tourOperatorBrief', id]`, NOT `['tourOperator', id]`:
 * that one holds a full `TourOperatorDetail` (label recovery, the trip-types root check),
 * and a list row there would break its readers. A brief is `null` once the lookup came
 * back without that operator (deleted, or not visible): the picker shows `#id`.
 *
 * Every list response the picker loads seeds the cache (`seedTenantBriefs`), so a
 * selection picked from the list never needs the lookup.
 */
import { type QueryClient, skipToken, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import * as topsApi from '@/api/tourOperators';

import { type BriefSource, type TenantBrief, briefsOfRow } from './clients';

/** The backend's cap on `ids=`. */
const MAX_IDS = 100;

export const tenantBriefKey = (id: number) => ['tourOperatorBrief', id] as const;

/** Cache what these rows say about their operators (and their folded sandboxes). */
export function seedTenantBriefs(qc: QueryClient, rows: readonly BriefSource[]): void {
  for (const row of rows) {
    for (const brief of briefsOfRow(row)) qc.setQueryData(tenantBriefKey(brief.id), brief);
  }
}

export interface UseTenantBriefsOptions {
  /** Hold the lookup back (e.g. until the picker's own list has landed and seeded the
   *  cache). Cached briefs are returned either way. */
  enabled?: boolean;
  /** Ids the caller already knows from elsewhere (a detail lookup): never batched. */
  known?: ReadonlySet<number>;
}

/** `ids` → what is known about each: a brief, `null` (looked up, not found) or absent
 *  (unknown yet). Missing ids are fetched in one request when `enabled`. */
export function useTenantBriefs(
  ids: readonly number[],
  { enabled = true, known }: UseTenantBriefsOptions = {},
): ReadonlyMap<number, TenantBrief | null> {
  const qc = useQueryClient();
  // Operators only: a sentinel (System is id 0) is never looked up.
  const unique = useMemo(
    () => [...new Set(ids)].filter((id) => Number.isSafeInteger(id) && id > 0).sort((a, b) => a - b),
    [ids.join(',')], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Observe each id's cache entry (no fetch of its own), so a seed re-renders us.
  const cached = useQueries({
    queries: unique.map((id) => ({
      queryKey: tenantBriefKey(id),
      queryFn: skipToken,
      staleTime: Infinity,
    })),
  });

  // Read the cache itself, not the observers: they hear of a seed one tick late, and a
  // render in between would batch-fetch ids the cache already holds.
  const missing = unique
    .filter((id) => qc.getQueryData(tenantBriefKey(id)) === undefined && !known?.has(id))
    .slice(0, MAX_IDS);
  const missingKey = missing.join(',');

  useQuery({
    queryKey: ['tourOperatorBriefs', missingKey],
    queryFn: async () => {
      const r = await topsApi.list({ ids: missing, pageSize: missing.length });
      seedTenantBriefs(qc, r.results);
      for (const id of missing) {
        if (qc.getQueryData(tenantBriefKey(id)) === undefined) qc.setQueryData(tenantBriefKey(id), null);
      }
      return missing.length;
    },
    enabled: enabled && missing.length > 0,
    staleTime: Infinity,
    retry: false,
  });

  const dataKey = cached.map((c) => (c.data === undefined ? '' : c.dataUpdatedAt)).join(',');
  return useMemo(() => {
    const out = new Map<number, TenantBrief | null>();
    unique.forEach((id, i) => {
      const data = cached[i]?.data as TenantBrief | null | undefined;
      if (data !== undefined) out.set(id, data);
    });
    return out;
  }, [unique, dataKey]); // eslint-disable-line react-hooks/exhaustive-deps
}
